import type { CountyAdapter, ParsedDocument } from "../types";

const splitNamesAndLegal = (
  text: string,
): { party1: string; party2: string; legal: string } => {
  const legalPatterns = [
    /S:\s*\d/,
    /Sub:\s*/,
    /Multiple\s/,
    /Lot:\s*\d/,
    /GovtLot:\s*\d/,
  ];
  let namesPart = text;
  let legal = "";
  for (const p of legalPatterns) {
    const m = text.match(p);
    if (m && m.index !== undefined) {
      if (!legal || m.index < text.indexOf(legal)) {
        namesPart = text.substring(0, m.index).trim();
        legal = text.substring(m.index).trim();
      }
    }
  }

  if (namesPart.includes("\t")) {
    const parts = namesPart.split("\t").filter(Boolean);
    return {
      party1: parts[0]?.trim() || "",
      party2: parts.slice(1).join(" ").trim(),
      legal,
    };
  }

  const spaceParts = namesPart.split(/\s{2,}/).filter(Boolean);
  if (spaceParts.length >= 2) {
    return {
      party1: spaceParts[0].trim(),
      party2: spaceParts.slice(1).join(" ").trim(),
      legal,
    };
  }

  for (const pattern of [
    /\b(LLC|INC|CORPORATION|COMPANY|LTD)\b/gi,
    /\b(TRUST)\b/gi,
  ]) {
    const matches = [...namesPart.matchAll(pattern)];
    if (matches.length === 1) {
      const m = matches[0];
      const endIdx = m.index! + m[0].length;
      if (endIdx < namesPart.length - 2) {
        return {
          party1: namesPart.substring(0, endIdx).trim(),
          party2: namesPart.substring(endIdx).trim(),
          legal,
        };
      }
    }
  }

  const suffixSplit = namesPart.match(
    /^(.+?\b(?:[A-Z]|JR|SR|II|III|IV)\b)\s*([A-Z][A-Z]+\s.+)$/,
  );
  if (suffixSplit) {
    return {
      party1: suffixSplit[1].trim(),
      party2: suffixSplit[2].trim(),
      legal,
    };
  }

  return { party1: namesPart.trim(), party2: "", legal };
};

const parse = (text: string): ParsedDocument[] => {
  const records: ParsedDocument[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const m = trimmed.match(
      /^(\d{10,15})LAND CONTRACT MEMORANDUM\s*(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:\d{2}:\d{2})\s+(AM|PM)(.+)$/i,
    );
    if (!m) continue;

    const documentNumber = m[1];
    const dateStr = m[2];
    const remainder = m[5].trim();
    const dateParts = dateStr.split("/");
    const filingDate =
      dateParts.length === 3
        ? `${dateParts[2]}-${dateParts[0].padStart(2, "0")}-${dateParts[1].padStart(2, "0")}`
        : dateStr;
    const { party1, party2, legal } = splitNamesAndLegal(remainder);
    if (!party1 || !documentNumber) continue;
    records.push({
      documentNumber,
      documentType: "LAND CONTRACT MEMORANDUM",
      filingDate,
      grantors: [party1],
      grantees: party2 ? [party2] : [],
      legalDescription: legal || undefined,
    });
  }
  return records;
};

export const antrimAdapter: CountyAdapter = {
  countyId: "antrim",
  countyLabel: "Antrim County, MI - Land Contract Parser",
  description:
    "Paste search results from Antrim County Register of Deeds website. Party 1 = Seller (Grantor), Party 2 = Buyer (Grantee). Automatically filters family transfers, bank transactions, and business-to-business deals.",
  placeholder: `Paste Antrim County search results here...

Copy all rows from the search results table. Each line should contain:
Document No + Document Type + Recorded Date + Party1 + Party2 + Legals

Example:
202100011637LAND CONTRACT MEMORANDUM12/13/2021 10:36:52 AMWITTBRODT KATHY MFERNHAUS INN LLCSub: HH NOBLES ADD...`,
  detectionReasons: ["Land Contract Memorandum", "Antrim County MI"],
  filterRules: [
    "Party 1 = Seller/Grantor (the person we want to contact)",
    "Party 2 = Buyer/Grantee",
    "Family transfers: Same last name on both sides → filtered out",
    "Business-to-business: Both parties are entities → filtered out",
    "Bank transactions: Any party is a bank/mortgage company → filtered out",
    "Church/IRA: Any party is a church or IRA → filtered out",
    "Duplicates: Existing document numbers in database are skipped",
  ],
  parse,
};
